import React, { ReactNode, useState } from 'react';
import { Button, Modal, Stack, Form, Row, Col } from 'react-bootstrap';
import { isValidFileSize, isValidFileType } from 'utils';
import useTranslate from 'hooks/useTranslate';
import {
	useBucketUrlMutation,
	useUpdateUserDetailsMutation,
	useUploadFileMutation,
} from 'services';
import { useFormik } from 'formik';
import { videoResumeInitialValues, videoResumeSchema } from 'constants/schemaValidations';
import toast from 'react-hot-toast';
import { VideoResumeInterface } from 'constants/interfaces';
import Loader from 'components/Common/Loader';
import useCommonStore from 'store/common';

const EditVideoResume: React.FC<any> = () => {
	const [thumbnail, setThumbnail] = useState(null);

	const { translate } = useTranslate();
	const { modalConfig = {} }: any = useCommonStore((state) => state);
	// state
	const [isUploading, setIsUploading] = useState(false);
	const [fileData, setFileData] = useState<File | null>(null);
	const [signurlData, setSignurlData] = useState<any>(null);

	// mutation
	const { mutateAsync: bucketUrlMutation } = useBucketUrlMutation();
	const { mutateAsync: uploadFileMutation } = useUploadFileMutation();
	const { mutateAsync: updateProfile } = useUpdateUserDetailsMutation();

	const formik = useFormik({
		initialValues: videoResumeInitialValues,
		enableReinitialize: true,
		validationSchema: videoResumeSchema(translate),
		onSubmit: async (values) => {
			setIsUploading(true);
			if (fileData && signurlData) {
				const requestOptions = {
					method: 'PUT',
					headers: {
						'Content-Type': fileData?.type,
					},
					body: fileData,
					redirect: 'follow',
				};

				await uploadFileMutation({
					url: signurlData?.url,
					requestOptions,
				});

				updateProfile({ ...values, saveType: 'videoResume' })
					.then((res: any) => {
						if (res?.success) {
							toast.success(res?.message);
						}
					})
					.finally(() => {
						setIsUploading(false);
						modalConfig?.onClose();
					});
			}
		},
	});

	const showingPreview = (event: any) => {
		const file = event.target.files?.[0];

		if (file) {
			// Check if file size is greater than 10 MB (10 * 1024 * 1024 bytes)
			if (file.size > 20 * 1024 * 1024) {
				return;
			}
			if (file.type.startsWith('video/')) {
				const reader = new FileReader();
				reader.onload = (e) => {
					const video = document.createElement('video');
					video.src = e.target?.result as string;
					video.onloadedmetadata = () => {
						video.currentTime = video.duration / 2;
					};

					video.onseeked = () => {
						const canvas: any = document.createElement('canvas');
						canvas.width = video.videoWidth;
						canvas.height = video.videoHeight;
						const ctx = canvas.getContext('2d');
						if (ctx) {
							ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
							setThumbnail(canvas.toDataURL('image/jpeg'));
						}
					};
				};
				reader.readAsDataURL(file);
			}
		}
	};

	const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
		setIsUploading(true);
		showingPreview(event);
		setSignurlData(null);
		setFileData(null);

		const file: any = event.target.files?.[0];
		if (file) {
			if (
				!isValidFileType(file, [
					'video/mp4',
					'video/quicktime',
					'video/x-msvideo',
					'video/x-ms-wmv',
					'video/x-matroska',
				]) ||
				!isValidFileSize(file, 20 * 1024 * 1024)
			) {
				setIsUploading(false);
				return;
			}

			const response: any = await bucketUrlMutation({
				extension: [file.name.split('.').pop()],
				type: 'RESUME',
				originalFileName: [file.name],
				contentType: [file.type],
			});
			setFileData(file);
			if (response?.data?.length > 0) {
				setSignurlData(response?.data[0]);
				formik.setFieldValue('videoResume', response?.data?.[0]?.location || '');
			}
		}
		setIsUploading(false);
	};

	const renderError = <T extends keyof VideoResumeInterface>(field: T) =>
		formik.touched[field] && formik.errors[field] ? (
			<span className="text-danger f-error">{formik.errors[field] as ReactNode}</span>
		) : null;

	return (
		<Modal.Body>
			<Button
				className="close-btn rounded-circle border-0 fw-bold position-absolute"
				onClick={() => {
					if (!isUploading) {
						modalConfig?.onClose();
					}
				}}
			>
				<i className="ri-close-line"></i>
			</Button>
			<Form className="edit-form" onSubmit={formik.handleSubmit}>
				<Stack className="gap-4">
					<h4 className="fw-bold mb-0">{translate('Profile.uploadVideoResume')}</h4>
					<Row>
						<Col md="12">
							<Form.Group className="mb-4 position-relative upload-resume">
								<Form.Control
									type="file"
									placeholder={translate('Extras.uploadVideo')}
									className="rounded-pill"
									name="videoResume"
									accept=".mp4, .mov, .avi, .wmv, .mkv"
									onChange={handleFileChange}
									onBlur={formik.handleBlur}
								/>
								<div className="upload-resume-content text-center">
									{thumbnail && <img src={thumbnail} alt="Video Thumbnail" loading="lazy" />}
									<div className="upload-resume-content-icon rounded-circle text-center mx-auto mb-2 mt-4">
										<i className="ri-upload-line"></i>
									</div>
									<p className="mb-0">{translate('ProfileModal.uploadVideo')}</p>
									<p className="mb-0 fs-12">
										( {translate('ProfileModal.sizeMatters', { size: '20' })} )
									</p>
								</div>
								{formik.submitCount > 0 && !formik.values.videoResume && renderError('videoResume')}
							</Form.Group>
						</Col>
					</Row>
					<div className="edit-modal-actions d-flex gap-4">
						{isUploading ? (
							<Loader />
						) : (
							<Button className="filled-btn rounded-pill" type="submit">
								{translate('Common.save')}
							</Button>
						)}
					</div>
				</Stack>
			</Form>
		</Modal.Body>
	);
};

export default EditVideoResume;
